'use client';

import { c as _c } from "react/compiler-runtime";
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import IconClose from '@/components/assets/icon_close.svg';
import OptimizedImage from '@/components/OptimizedImage';
import { useMoreVideosPanelContext } from '@/context/MoreVideosPanelContext';
import { useUAContext } from '@/context/UAContext';
import { useWorldDetailsContext } from '@/context/WorldDetailsContext';
import { useIsEmbedded } from '@/hooks/useIsEmbedded';
import { extractRoomIdFromUrl } from '@/lib/cms';
import { withFull3d } from '@/lib/url';
import { cn } from '@/lib/utils';
import roomDefaultImage from '@/public/images/room-default.png';
import type { MoreVideoItem } from '@/types/more-videos';
const ANIMATION_DURATION_S = 0.3;
const MAX_VISIBLE_ITEMS = 3;
const getVisibleMoreVideoItems = (items: MoreVideoItem[]) => items.slice(0, MAX_VISIBLE_ITEMS);
type LayoutVariant = 'embed' | 'mobile' | 'desktop';
type VideoCardProps = {
  item: MoreVideoItem;
  isEmbedded: boolean;
  showTitle?: boolean;
};
type PanelProps = {
  items: MoreVideoItem[];
  isEmbedded: boolean;
  t: ReturnType<typeof useTranslations>;
};
type CloseablePanelProps = PanelProps & {
  closePanel: () => void;
};
function VideoCard(t0) {
  const $ = _c(23);
  const {
    item,
    isEmbedded,
    showTitle: t1
  } = t0;
  const showTitle = t1 === undefined ? false : t1;
  const thumbnailSrc = item.imageUrl || roomDefaultImage.src;
  let t2;
  if ($[0] !== isEmbedded || $[1] !== item.hubsUrl) {
    const roomHref = extractRoomIdFromUrl(item.hubsUrl);
    t2 = isEmbedded ? withFull3d(roomHref) : roomHref;
    $[0] = isEmbedded;
    $[1] = item.hubsUrl;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const href = t2;
  const t3 = showTitle && "flex flex-col gap-2";
  let t4;
  if ($[3] !== t3) {
    t4 = cn("group focus-visible:ring-vive-border-focus focus-visible:ring-offset-background block min-w-0 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none", t3);
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      borderRadius: "clamp(6px, 2.5cqw, 25px)"
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== item.title || $[7] !== thumbnailSrc) {
    t6 = <OptimizedImage src={thumbnailSrc} alt={item.title} fill={true} className="object-cover" sizes="(max-width: 1024px) 200px, 33vw" />;
    $[6] = item.title;
    $[7] = thumbnailSrc;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== item.title || $[10] !== showTitle) {
    t7 = !showTitle && <><div className="absolute inset-0 bg-linear-to-b from-black/50 to-transparent transition-opacity group-hover:opacity-0 group-focus-visible:opacity-0" /><div className="absolute inset-0 bg-linear-to-t from-black/50 to-transparent opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100" /><div className="absolute inset-0 flex min-w-0 flex-col justify-end p-4 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"><p className="w-full truncate text-lg leading-6.5 font-extrabold tracking-[0.1px] xl:text-2xl xl:leading-8">{item.title}</p></div></>;
    $[9] = item.title;
    $[10] = showTitle;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t6 || $[13] !== t7) {
    t8 = <div className="relative aspect-570/331 w-full overflow-hidden" style={t5}>{t6}{t7}</div>;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== item.title || $[16] !== showTitle) {
    t9 = showTitle && <p className="text-vive-white line-clamp-2 min-h-10 text-sm leading-5 font-bold">{item.title}</p>;
    $[15] = item.title;
    $[16] = showTitle;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== href || $[19] !== t4 || $[20] !== t8 || $[21] !== t9) {
    t10 = <Link href={href} className={t4}>{t8}{t9}</Link>;
    $[18] = href;
    $[19] = t4;
    $[20] = t8;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  return t10;
}
function EmbedPanel(t0) {
  const $ = _c(56);
  const {
    items,
    isEmbedded,
    closePanel,
    t
  } = t0;
  let T0;
  let t1;
  let t10;
  let t11;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== closePanel || $[1] !== isEmbedded || $[2] !== items || $[3] !== t) {
    const visibleItems = getVisibleMoreVideoItems(items);
    const count = visibleItems.length;
    const gridTemplateColumns = `repeat(${count}, max(200px, calc(${"(100cqw - 5rem) / 3"})))`;
    T0 = motion.div;
    t6 = "bg-background/85 pointer-events-auto absolute inset-0 flex flex-col gap-6 overflow-hidden p-6 backdrop-blur-md";
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = {
        opacity: 0
      };
      t8 = {
        opacity: 1
      };
      t9 = {
        opacity: 0
      };
      t10 = {
        duration: ANIMATION_DURATION_S,
        ease: "easeOut"
      };
      $[16] = t10;
      $[17] = t7;
      $[18] = t8;
      $[19] = t9;
    } else {
      t10 = $[16];
      t7 = $[17];
      t8 = $[18];
      t9 = $[19];
    }
    let t12;
    if ($[20] !== t) {
      t12 = t("media-preview-modal.close");
      $[20] = t;
      $[21] = t12;
    } else {
      t12 = $[21];
    }
    let t13;
    if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = <IconClose className="text-vive-white size-6" />;
      $[22] = t13;
    } else {
      t13 = $[22];
    }
    if ($[23] !== closePanel || $[24] !== t12) {
      t11 = <button type="button" className="z-interactive hover:bg-vive-ghost-white absolute top-4 right-4 cursor-pointer rounded-full p-1.5 transition-colors" onClick={closePanel} aria-label={t12}>{t13}</button>;
      $[23] = closePanel;
      $[24] = t12;
      $[25] = t11;
    } else {
      t11 = $[25];
    }
    t4 = "absolute right-6 bottom-4 left-6 flex flex-col gap-3 overflow-x-auto overflow-y-hidden";
    let t14;
    if ($[26] !== gridTemplateColumns) {
      t14 = {
        gridTemplateColumns
      };
      $[26] = gridTemplateColumns;
      $[27] = t14;
    } else {
      t14 = $[27];
    }
    let t15;
    if ($[28] !== t) {
      t15 = t("more-videos-panel.title");
      $[28] = t;
      $[29] = t15;
    } else {
      t15 = $[29];
    }
    let t16;
    if ($[30] !== t15) {
      t16 = <h2 className="text-vive-white text-2xl leading-7 font-extrabold tracking-[0.12px] xl:text-[32px] xl:leading-9">{t15}</h2>;
      $[30] = t15;
      $[31] = t16;
    } else {
      t16 = $[31];
    }
    if ($[32] !== t14 || $[33] !== t16) {
      t5 = <div className="grid w-max min-w-full justify-start" style={t14}>{t16}</div>;
      $[32] = t14;
      $[33] = t16;
      $[34] = t5;
    } else {
      t5 = $[34];
    }
    t1 = "mb-2 grid w-max min-w-full justify-start gap-4";
    if ($[35] !== gridTemplateColumns) {
      t2 = {
        gridTemplateColumns
      };
      $[35] = gridTemplateColumns;
      $[36] = t2;
    } else {
      t2 = $[36];
    }
    let t17;
    if ($[37] !== isEmbedded) {
      t17 = item => <VideoCard key={item.hubsUrl} item={item} isEmbedded={isEmbedded} showTitle={true} />;
      $[37] = isEmbedded;
      $[38] = t17;
    } else {
      t17 = $[38];
    }
    t3 = visibleItems.map(t17);
    $[0] = closePanel;
    $[1] = isEmbedded;
    $[2] = items;
    $[3] = t;
    $[4] = T0;
    $[5] = t1;
    $[6] = t10;
    $[7] = t11;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
    $[15] = t9;
  } else {
    T0 = $[4];
    t1 = $[5];
    t10 = $[6];
    t11 = $[7];
    t2 = $[8];
    t3 = $[9];
    t4 = $[10];
    t5 = $[11];
    t6 = $[12];
    t7 = $[13];
    t8 = $[14];
    t9 = $[15];
  }
  let t12;
  if ($[39] !== t1 || $[40] !== t2 || $[41] !== t3) {
    t12 = <div className={t1} style={t2}>{t3}</div>;
    $[39] = t1;
    $[40] = t2;
    $[41] = t3;
    $[42] = t12;
  } else {
    t12 = $[42];
  }
  let t13;
  if ($[43] !== t12 || $[44] !== t4 || $[45] !== t5) {
    t13 = <div className={t4}>{t5}{t12}</div>;
    $[43] = t12;
    $[44] = t4;
    $[45] = t5;
    $[46] = t13;
  } else {
    t13 = $[46];
  }
  let t14;
  if ($[47] !== T0 || $[48] !== t10 || $[49] !== t11 || $[50] !== t13 || $[51] !== t6 || $[52] !== t7 || $[53] !== t8 || $[54] !== t9) {
    t14 = <T0 className={t6} initial={t7} animate={t8} exit={t9} transition={t10}>{t11}{t13}</T0>;
    $[47] = T0;
    $[48] = t10;
    $[49] = t11;
    $[50] = t13;
    $[51] = t6;
    $[52] = t7;
    $[53] = t8;
    $[54] = t9;
    $[55] = t14;
  } else {
    t14 = $[55];
  }
  return t14;
}
type DesktopPanelProps = PanelProps & {
  anchorBottom: number | null;
  isEnded: boolean;
};
function DesktopPanel(t0) {
  const $ = _c(48);
  const {
    items,
    isEmbedded,
    anchorBottom,
    isEnded,
    t
  } = t0;
  let T0;
  let t1;
  let t10;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== anchorBottom || $[1] !== isEmbedded || $[2] !== isEnded || $[3] !== items || $[4] !== t) {
    const visibleItems = getVisibleMoreVideoItems(items);
    const count = visibleItems.length;
    const panelWidth = `calc(${count} * (${"(100cqw - 9rem) / 3"}) + ${count - 1 + 4}rem)`;
    const gridTemplateColumns = `repeat(${count}, calc(${"(100cqw - 9rem) / 3"}))`;
    T0 = motion.div;
    t4 = "bg-vive-surface-normal/40 pointer-events-auto absolute left-0 mx-6 flex flex-col gap-4 rounded-lg p-8 backdrop-blur-2xl";
    const t11 = anchorBottom ?? 118;
    if ($[16] !== panelWidth || $[17] !== t11) {
      t5 = {
        bottom: t11,
        width: panelWidth
      };
      $[16] = panelWidth;
      $[17] = t11;
      $[18] = t5;
    } else {
      t5 = $[18];
    }
    if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = {
        opacity: 0,
        y: 24
      };
      t7 = {
        opacity: 1,
        y: 0
      };
      t8 = {
        opacity: 0,
        y: 24
      };
      t9 = {
        duration: ANIMATION_DURATION_S,
        ease: "easeOut"
      };
      $[19] = t6;
      $[20] = t7;
      $[21] = t8;
      $[22] = t9;
    } else {
      t6 = $[19];
      t7 = $[20];
      t8 = $[21];
      t9 = $[22];
    }
    const t12 = isEnded ? "text-xl leading-7 tracking-[0.12px] xl:text-2xl xl:leading-7" : "text-[28px] leading-[30px] tracking-[0.15px] xl:text-[38px] xl:leading-10";
    let t13;
    if ($[23] !== t12) {
      t13 = cn("text-vive-white text-left font-extrabold", t12);
      $[23] = t12;
      $[24] = t13;
    } else {
      t13 = $[24];
    }
    let t14;
    if ($[25] !== t) {
      t14 = t("more-videos-panel.title");
      $[25] = t;
      $[26] = t14;
    } else {
      t14 = $[26];
    }
    if ($[27] !== t13 || $[28] !== t14) {
      t10 = <h2 className={t13}>{t14}</h2>;
      $[27] = t13;
      $[28] = t14;
      $[29] = t10;
    } else {
      t10 = $[29];
    }
    t1 = "grid gap-4";
    if ($[30] !== gridTemplateColumns) {
      t2 = {
        gridTemplateColumns
      };
      $[30] = gridTemplateColumns;
      $[31] = t2;
    } else {
      t2 = $[31];
    }
    let t15;
    if ($[32] !== isEmbedded) {
      t15 = item => <VideoCard key={item.hubsUrl} item={item} isEmbedded={isEmbedded} />;
      $[32] = isEmbedded;
      $[33] = t15;
    } else {
      t15 = $[33];
    }
    t3 = visibleItems.map(t15);
    $[0] = anchorBottom;
    $[1] = isEmbedded;
    $[2] = isEnded;
    $[3] = items;
    $[4] = t;
    $[5] = T0;
    $[6] = t1;
    $[7] = t10;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
    $[15] = t9;
  } else {
    T0 = $[5];
    t1 = $[6];
    t10 = $[7];
    t2 = $[8];
    t3 = $[9];
    t4 = $[10];
    t5 = $[11];
    t6 = $[12];
    t7 = $[13];
    t8 = $[14];
    t9 = $[15];
  }
  let t11;
  if ($[34] !== t1 || $[35] !== t2 || $[36] !== t3) {
    t11 = <div className={t1} style={t2}>{t3}</div>;
    $[34] = t1;
    $[35] = t2;
    $[36] = t3;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  let t12;
  if ($[38] !== T0 || $[39] !== t10 || $[40] !== t11 || $[41] !== t4 || $[42] !== t5 || $[43] !== t6 || $[44] !== t7 || $[45] !== t8 || $[46] !== t9) {
    t12 = <T0 className={t4} style={t5} initial={t6} animate={t7} exit={t8} transition={t9}>{t10}{t11}</T0>;
    $[38] = T0;
    $[39] = t10;
    $[40] = t11;
    $[41] = t4;
    $[42] = t5;
    $[43] = t6;
    $[44] = t7;
    $[45] = t8;
    $[46] = t9;
    $[47] = t12;
  } else {
    t12 = $[47];
  }
  return t12;
}
type MobilePanelProps = CloseablePanelProps & {
  isLandscape: boolean;
};
function MobilePanel(t0) {
  const $ = _c(54);
  const {
    items,
    isEmbedded,
    closePanel,
    isLandscape,
    t
  } = t0;
  let T0;
  let t1;
  let t10;
  let t11;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== closePanel || $[1] !== isEmbedded || $[2] !== isLandscape || $[3] !== items || $[4] !== t) {
    const visibleItems = getVisibleMoreVideoItems(items);
    const count = visibleItems.length;
    const gridTemplateColumns = `repeat(${count}, max(200px, calc(${"(100cqw - 5rem) / 3"})))`;
    T0 = motion.div;
    t6 = "bg-background/85 pointer-events-auto absolute inset-0 overflow-hidden backdrop-blur-md";
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = {
        opacity: 0
      };
      t8 = {
        opacity: 1
      };
      t9 = {
        opacity: 0
      };
      t10 = {
        duration: ANIMATION_DURATION_S,
        ease: "easeOut"
      };
      $[17] = t10;
      $[18] = t7;
      $[19] = t8;
      $[20] = t9;
    } else {
      t10 = $[17];
      t7 = $[18];
      t8 = $[19];
      t9 = $[20];
    }
    let t12;
    if ($[21] !== t) {
      t12 = t("media-preview-modal.close");
      $[21] = t;
      $[22] = t12;
    } else {
      t12 = $[22];
    }
    let t13;
    if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = <IconClose className="text-vive-white size-5" />;
      $[23] = t13;
    } else {
      t13 = $[23];
    }
    if ($[24] !== closePanel || $[25] !== t12) {
      t11 = <button type="button" className="z-interactive hover:bg-vive-ghost-white absolute top-3 right-3 cursor-pointer rounded-full p-1.5 transition-colors" onClick={closePanel} aria-label={t12}>{t13}</button>;
      $[24] = closePanel;
      $[25] = t12;
      $[26] = t11;
    } else {
      t11 = $[26];
    }
    const t14 = isLandscape ? "gap-2" : "gap-3";
    if ($[27] !== t14) {
      t4 = cn("absolute right-4 bottom-4 left-4 flex flex-col overflow-x-auto overflow-y-hidden", t14);
      $[27] = t14;
      $[28] = t4;
    } else {
      t4 = $[28];
    }
    let t15;
    if ($[29] !== t) {
      t15 = t("more-videos-panel.title");
      $[29] = t;
      $[30] = t15;
    } else {
      t15 = $[30];
    }
    if ($[31] !== t15) {
      t5 = <h2 className="text-vive-white text-base leading-6 font-bold tracking-[0.25px]">{t15}</h2>;
      $[31] = t15;
      $[32] = t5;
    } else {
      t5 = $[32];
    }
    t1 = "grid w-max min-w-full justify-start gap-3 pb-1";
    if ($[33] !== gridTemplateColumns) {
      t2 = {
        gridTemplateColumns
      };
      $[33] = gridTemplateColumns;
      $[34] = t2;
    } else {
      t2 = $[34];
    }
    let t16;
    if ($[35] !== isEmbedded) {
      t16 = item => <VideoCard key={item.hubsUrl} item={item} isEmbedded={isEmbedded} showTitle={true} />;
      $[35] = isEmbedded;
      $[36] = t16;
    } else {
      t16 = $[36];
    }
    t3 = visibleItems.map(t16);
    $[0] = closePanel;
    $[1] = isEmbedded;
    $[2] = isLandscape;
    $[3] = items;
    $[4] = t;
    $[5] = T0;
    $[6] = t1;
    $[7] = t10;
    $[8] = t11;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
  } else {
    T0 = $[5];
    t1 = $[6];
    t10 = $[7];
    t11 = $[8];
    t2 = $[9];
    t3 = $[10];
    t4 = $[11];
    t5 = $[12];
    t6 = $[13];
    t7 = $[14];
    t8 = $[15];
    t9 = $[16];
  }
  let t12;
  if ($[37] !== t1 || $[38] !== t2 || $[39] !== t3) {
    t12 = <div className={t1} style={t2}>{t3}</div>;
    $[37] = t1;
    $[38] = t2;
    $[39] = t3;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  let t13;
  if ($[41] !== t12 || $[42] !== t4 || $[43] !== t5) {
    t13 = <div className={t4}>{t5}{t12}</div>;
    $[41] = t12;
    $[42] = t4;
    $[43] = t5;
    $[44] = t13;
  } else {
    t13 = $[44];
  }
  let t14;
  if ($[45] !== T0 || $[46] !== t10 || $[47] !== t11 || $[48] !== t13 || $[49] !== t6 || $[50] !== t7 || $[51] !== t8 || $[52] !== t9) {
    t14 = <T0 className={t6} initial={t7} animate={t8} exit={t9} transition={t10}>{t11}{t13}</T0>;
    $[45] = T0;
    $[46] = t10;
    $[47] = t11;
    $[48] = t13;
    $[49] = t6;
    $[50] = t7;
    $[51] = t8;
    $[52] = t9;
    $[53] = t14;
  } else {
    t14 = $[53];
  }
  return t14;
}
export default function MoreVideosPanel(t0) {
  const $ = _c(27);
  const {
    isMobile
  } = t0;
  const {
    isOpen,
    items,
    reason,
    anchorBottom,
    closePanel
  } = useMoreVideosPanelContext();
  const {
    isMobileLandscape
  } = useUAContext();
  const {
    isEmbeddedRequest
  } = useWorldDetailsContext();
  const isEmbedded = useIsEmbedded(isEmbeddedRequest);
  const t = useTranslations();
  const variant = isEmbedded ? "embed" : isMobile ? "mobile" : "desktop";
  const show = isOpen && items.length > 0;
  let t1;
  if ($[0] !== closePanel || $[1] !== isEmbedded || $[2] !== items || $[3] !== show || $[4] !== t || $[5] !== variant) {
    t1 = show && variant === "embed" && <EmbedPanel key="embed" items={items} isEmbedded={isEmbedded} closePanel={closePanel} t={t} />;
    $[0] = closePanel;
    $[1] = isEmbedded;
    $[2] = items;
    $[3] = show;
    $[4] = t;
    $[5] = variant;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== closePanel || $[8] !== isEmbedded || $[9] !== isMobileLandscape || $[10] !== items || $[11] !== show || $[12] !== t || $[13] !== variant) {
    t2 = show && variant === "mobile" && <MobilePanel key="mobile" items={items} isEmbedded={isEmbedded} closePanel={closePanel} isLandscape={isMobileLandscape} t={t} />;
    $[7] = closePanel;
    $[8] = isEmbedded;
    $[9] = isMobileLandscape;
    $[10] = items;
    $[11] = show;
    $[12] = t;
    $[13] = variant;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  let t3;
  if ($[15] !== anchorBottom || $[16] !== isEmbedded || $[17] !== items || $[18] !== reason || $[19] !== show || $[20] !== t || $[21] !== variant) {
    t3 = show && variant === "desktop" && <DesktopPanel key="desktop" items={items} isEmbedded={isEmbedded} anchorBottom={anchorBottom} isEnded={reason === "ended"} t={t} />;
    $[15] = anchorBottom;
    $[16] = isEmbedded;
    $[17] = items;
    $[18] = reason;
    $[19] = show;
    $[20] = t;
    $[21] = variant;
    $[22] = t3;
  } else {
    t3 = $[22];
  }
  let t4;
  if ($[23] !== t1 || $[24] !== t2 || $[25] !== t3) {
    t4 = <div className="z-navigation @container pointer-events-none absolute inset-0"><AnimatePresence>{t1}{t2}{t3}</AnimatePresence></div>;
    $[23] = t1;
    $[24] = t2;
    $[25] = t3;
    $[26] = t4;
  } else {
    t4 = $[26];
  }
  return t4;
}